import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError } from '@/lib/api';
import type {
  CalDavConnectInput,
  CalendarAccount,
  CalendarStatus,
  CalendarSummary,
  CalendarUpdate,
} from './types';

const ACCOUNTS_KEY = ['calendars', 'accounts'] as const;
const STATUS_KEY = ['calendars', 'status'] as const;

export function useCalendarStatus() {
  return useQuery({
    queryKey: STATUS_KEY,
    queryFn: () => api<CalendarStatus>('/api/calendars/status'),
    staleTime: 60_000,
  });
}

export function useCalendarAccounts() {
  return useQuery({
    queryKey: ACCOUNTS_KEY,
    queryFn: () => api<CalendarAccount[]>('/api/calendars/accounts'),
  });
}

export function useStartGoogle() {
  return useMutation<{ authorization_url: string }, ApiError>({
    mutationFn: () =>
      api<{ authorization_url: string }>('/api/calendars/google/start', {
        method: 'POST',
      }),
  });
}

export function useConnectCalDav() {
  const qc = useQueryClient();
  return useMutation<CalendarAccount, ApiError, CalDavConnectInput>({
    mutationFn: (input) =>
      api<CalendarAccount>('/api/calendars/caldav', { method: 'POST', json: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ACCOUNTS_KEY });
    },
  });
}

export function useDisconnectAccount() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, number>({
    mutationFn: (id) =>
      api<void>(`/api/calendars/accounts/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ACCOUNTS_KEY });
    },
  });
}

export function useSyncAccount() {
  const qc = useQueryClient();
  return useMutation<{ status: string }, ApiError, number>({
    mutationFn: (id) =>
      api<{ status: string }>(`/api/calendars/accounts/${id}/sync`, {
        method: 'POST',
      }),
    onSuccess: () => {
      // Sync is async on the worker; refresh shortly so last_synced_at updates.
      window.setTimeout(() => {
        void qc.invalidateQueries({ queryKey: ACCOUNTS_KEY });
      }, 2500);
    },
  });
}

export function useToggleCalendar() {
  const qc = useQueryClient();
  return useMutation<CalendarSummary, ApiError, CalendarUpdate>({
    mutationFn: ({ calendarId, ...patch }) =>
      api<CalendarSummary>(`/api/calendars/calendars/${calendarId}`, {
        method: 'PATCH',
        json: patch,
      }),
    onSuccess: (updated) => {
      qc.setQueryData<CalendarAccount[]>(ACCOUNTS_KEY, (old) => {
        if (!old) return old;
        return old.map((account) => ({
          ...account,
          calendars: account.calendars.map((cal) =>
            cal.id === updated.id ? { ...cal, ...updated } : cal,
          ),
        }));
      });
      void qc.invalidateQueries({ queryKey: ACCOUNTS_KEY });
      // Plan (and any other surface) reads event colour from the calendar.
      void qc.invalidateQueries({ queryKey: ['calendars', 'events'] });
    },
  });
}
